import {
  useCallback,
  Children,
  cloneElement,
  Fragment,
  useEffect,
  useState,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";

import { handled, logRejection } from "@/helpers";
import { usePortalMount } from "@/hooks/portal";

import Button from "./input/button";
import Input from "./input/input";

// _toggle is wired to a React onClick, to a DOM keypress event and to a cloned
// child's onClick, and only ever calls preventDefault on what it is handed.
interface TogglableEvent {
  preventDefault: () => void;
}

interface ConfirmFnResult {
  toggle?: boolean;
}

export interface ConfirmProps {
  question?: string;
  body?: any;
  fn_args: any;
  children: any;
  in_context_menu?: boolean;
  required_phrase?: string;
  prevent_default?: boolean;
  // Callers may fire and forget, or resolve a result asking the modal to stay open.
  confirmFn: (args: any) => void | Promise<ConfirmFnResult | void>;
  contextFn?: () => void;
  // Awaited before the modal closes, so an async caller finishes first.
  callbackFn?: () => void | Promise<void>;
  onOpen?: () => void;
}

export default function Confirm({
  question = "Are you sure?",
  body,
  fn_args,
  children,
  in_context_menu,
  required_phrase,
  prevent_default,
  confirmFn,
  contextFn,
  callbackFn,
  onOpen,
}: ConfirmProps) {
  const [active, setActive] = useState(false);
  const [phrase, setPhrase] = useState("");
  const modal_element = usePortalMount("modal");

  const _toggle = useCallback(
    (e?: TogglableEvent) => {
      if (e && prevent_default) {
        e.preventDefault();
      }
      if (!active) {
        onOpen?.();
      }
      setActive(!active);
    },
    [active, prevent_default, onOpen],
  );

  const _handleConfirm = useCallback(
    async (ev: MouseEvent | KeyboardEvent) => {
      const res = await confirmFn(fn_args);
      await callbackFn?.();
      if (res?.toggle !== false) {
        _toggle(ev);
      }
    },
    [confirmFn, fn_args, callbackFn, _toggle],
  );

  useEffect(() => {
    if (!modal_element || !active) return;
    modal_element.classList.add("modal-is-active");
    return () => {
      modal_element.classList.remove("modal-is-active");
    };
  }, [active, modal_element]);

  useEffect(() => {
    if (!active) return;
    const handleKeypress = (ev: KeyboardEvent) => {
      if (ev.keyCode === 13) {
        _handleConfirm(ev).catch(logRejection);
      }
    };
    window.addEventListener("keypress", handleKeypress);
    return () => {
      window.removeEventListener("keypress", handleKeypress);
    };
  }, [active, _handleConfirm]);

  const child = Children.only(children);
  let first_element: React.ReactNode | null = null;

  if (in_context_menu) {
    const specialToggle = (ev: Event) => {
      _toggle(ev);
      contextFn?.();
    };
    first_element = cloneElement(child, {
      className: "context-menu__item",
      onClick: specialToggle,
    });
  } else {
    first_element = cloneElement(child, {
      onClick: _toggle,
      key: "c1",
    });
  }

  return (
    <Fragment>
      {first_element}
      {active &&
        modal_element &&
        createPortal(
          <div className="modal-window overscroll-contain rounded-xl bg-midnight-blue p-4 drop-shadow">
            <div className="mb-4">
              <p className="font-outfit text-xl">{question}</p>
              {body && <div>{body}</div>}
            </div>
            {required_phrase && (
              <div className="mb-4">
                <p>To confirm, enter {`"${required_phrase}"`} below.</p>
                <Input
                  value={phrase}
                  placeholder={required_phrase}
                  onChange={(event) => setPhrase(event.target.value)}
                />
              </div>
            )}
            <div className="flex justify-end gap-4">
              <Button
                type="submit"
                onClick={handled(_handleConfirm)}
                disabled={!!(required_phrase && phrase !== required_phrase)}
              >
                Ok
              </Button>
              <Button onClick={_toggle}>Cancel</Button>
            </div>
          </div>,
          modal_element,
        )}
    </Fragment>
  );
}
